import { all, call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { getAutomatedBroadcasts, getAudiences, updateAutomatedBroadcast } from './api';
import {
  FETCH_AUTOMATED_BROADCAST_REQUEST,
  FETCH_AUTOMATED_BROADCAST_SUCCESS,
  FETCH_AUTOMATED_BROADCAST_FAILURE,
  FETCH_AUDIENCES_REQUEST,
  FETCH_AUDIENCES_SUCCESS,
  FETCH_AUDIENCES_FAILURE,
  SEARCH_AUTOMATED_BROADCAST_REQUEST,
  SEARCH_AUTOMATED_BROADCAST_SUCCESS,
  HANDLE_DROP_FAILURE,
  HANDLE_DROP_SUCCESS,
  HANDLE_DROP_REQUEST,
  HANDLE_REMOVE_FAILURE,
  HANDLE_REMOVE_REQUEST,
  HANDLE_REMOVE_SUCCESS,
  UPDATE_AUTOMATED_REQUEST,
  UPDATE_AUTOMATED_SUCCESS,
  UPDATE_AUTOMATED_FAILURE,
  UNDO_FAILURE,
  UNDO_REQUEST,
  UNDO_SUCCESS,
  PUSH_INTO_UNDO_STACK,
  REDO_REQUEST,
  CHANGE_PROPERTIES
} from './constants';

// Select program state
const onboarderState = state => state.onboarder;

/**
 * Fetch list automated broadcast
 * @param action
 */
function* fetchAutomatedBroadcasts(action) {
  try {
    const automatedBroadcasts = yield call(getAutomatedBroadcasts, action.payload.token);
    yield put({
      type: FETCH_AUTOMATED_BROADCAST_SUCCESS,
      automatedBroadcasts
    });
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: FETCH_AUTOMATED_BROADCAST_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
  }
}

/**
 * Fetch list audiences
 * @param action
 */
function* fetchAudiences(action) {
  try {
    const audiences = yield call(getAudiences, action.payload.token);
    yield put({
      type: FETCH_AUDIENCES_SUCCESS,
      audiences
    });
  } catch (e) {
    console.log(e.response || e);
    yield put({
      type: FETCH_AUDIENCES_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
  }
}

/**
 * Search automated broadcast
 * @param action
 */
function* searchAutomatedBroadcast(action) {
  try {
    const state = yield select(onboarderState);
    const automatedBroadcasts = state.get('automatedBroadcasts').toJS();
    const searchResult = automatedBroadcasts.filter((ab) => {
      if (!ab.name) { return false; }
      return ab.name.toLowerCase().indexOf(action.payload.searchText.toLowerCase().trim()) !== -1;
    });
    yield put({
      type: SEARCH_AUTOMATED_BROADCAST_SUCCESS,
      searchResult
    });
  } catch (e) {
    console.log(e.response || e);
  }
}

/**
 * Update automated broadcast
 */
function* updateAutomated(automated) {
  try {
    if (!automated) {
      throw new Error('Automated broadcast not found!');
    }
    yield put({ type: UPDATE_AUTOMATED_REQUEST });
    const saved = yield updateAutomatedBroadcast(localStorage.getItem('token'), automated);
    yield put({ type: UPDATE_AUTOMATED_SUCCESS });
    return saved;
  } catch (e) {
    yield put({ type: UPDATE_AUTOMATED_FAILURE });
    throw e;
  }
}

/**
 * Push work state into Undo-Redo stack
 */
function* pushIntoUndoStack(currentAutomated, nextAutomated) {
  try {
    if (
      !currentAutomated
      || !nextAutomated
      || !Array.isArray(currentAutomated)
      || !Array.isArray(nextAutomated)
    ) { return; }
    const state = yield select(onboarderState);
    const newState = state.get('stateHistory').toJS();
    const recovery = newState.recovery.filter((rec, index) => index <= newState.currentIndex);
    recovery.push({
      before: currentAutomated,
      after: nextAutomated
    });
    newState.recovery = recovery;
    newState.currentIndex += 1;
    newState.canUndo = newState.currentIndex > -1;
    newState.canRedo = false;
    // Push into stack
    yield put({
      type: PUSH_INTO_UNDO_STACK,
      stateHistory: newState
    });
    // Save automated broadcast
    yield all(nextAutomated.map(automated => updateAutomated(automated)));
  } catch (e) {
    throw e;
  }
}

/**
 * Handle dropping node
 * @param action
 * @returns {IterableIterator<*>}
 */
function* handleDrop(action) {
  try {
    const { source, target } = action.payload;
    const state = yield select(onboarderState);
    const automatedBroadcasts = state.get('automatedBroadcasts').toJS();
    const newABs = state.get('automatedBroadcasts').toJS();
    // Automated updated
    const nextAutomated = [];
    // Current automated
    const currentAutomated = [];
    // Remove AB from source node
    if (source.relation) {
      const abIndex = newABs.findIndex(item => item.id === source.relation.abId);
      if (abIndex > -1) {
        const automated = newABs[abIndex];
        const content = automated.contents.find(item => item.id === source.relation.contentId);
        if (content) {
          const cta = content.ctas.find(item => item.id === source.relation.ctaId);
          if (cta) {
            cta.linkTo.broadcast = '';
          } else {
            content.endOfContent.broadcast = '';
          }
          // Push automated to current automated
          currentAutomated.push(automatedBroadcasts[abIndex]);
          // Push automated to list automated updated
          nextAutomated.push(automated);
        }
      }
    }
    // Add AB to target node
    //   + Case 1: CTA/EFC node
    //   + Case 2: INIT node
    if (target.relation && target.relation.abId) {
      const abIndex = newABs.findIndex(item => item.id === target.relation.abId);
      if (abIndex > -1) {
        const automated = newABs[abIndex];
        const content = automated.contents.find(item => item.id === target.relation.contentId);
        if (content) {
          const cta = content.ctas.find(item => item.id === target.relation.ctaId);
          if (cta) {
            cta.linkTo.broadcast = source.item.id;
          } else {
            content.endOfContent.broadcast = source.item.id;
          }
          // Push automated to current automated
          currentAutomated.push(automatedBroadcasts[abIndex]);
          // Push automated to list automated updated
          nextAutomated.push(automated);
        }
      }
    } else {
      const abIndex = newABs.findIndex(item => item.id === source.item.id);
      if (abIndex > -1) {
        const automated = newABs[abIndex];
        automated.segment = localStorage.getItem('segmentId');
        // Push automated to current automated
        currentAutomated.push(automatedBroadcasts[abIndex]);
        // Push automated to list automated updated
        nextAutomated.push(automated);
      }
    }
    yield put({
      type: HANDLE_DROP_SUCCESS,
      automatedBroadcasts: newABs
    });
    // Push into UNDO_REDO list
    yield pushIntoUndoStack(currentAutomated, nextAutomated);
  } catch (e) {
    console.error(e.response || e);
    yield put({
      type: HANDLE_DROP_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
    // TODO: It should be return previous state when dropping error
  }
}

/**
 * Handle remove ab node
 * @param action
 * @returns {IterableIterator<*>}
 */
function* handleRemove(action) {
  try {
    const { item, relation } = action.payload;
    const state = yield select(onboarderState);
    const automatedBroadcasts = state.get('automatedBroadcasts').toJS();
    const newABs = state.get('automatedBroadcasts').toJS();
    // Automated updated
    const nextAutomated = [];
    // Current automated
    const currentAutomated = [];
    // Remove broadcast id
    //   + Case 1: CTA/EFC node
    //   + Case 2: INIT node
    if (relation && relation.abId) {
      const abIndex = newABs.findIndex(ab => ab.id === relation.abId);
      if (abIndex > -1) {
        const automated = newABs[abIndex];
        const content = automated.contents.find(ab => ab.id === relation.contentId);
        if (content) {
          const cta = content.ctas.find(ab => ab.id === relation.ctaId);
          if (cta) {
            cta.linkTo.broadcast = '';
          } else {
            content.endOfContent.broadcast = '';
          }
          // Push automated to current automated
          currentAutomated.push(automatedBroadcasts[abIndex]);
          // Push automated to list automated updated
          nextAutomated.push(automated);
        }
      }
    } else {
      const abIndex = newABs.findIndex(ab => ab.id === item.id);
      if (abIndex > -1) {
        const automated = newABs[abIndex];
        automated.segment = '';
        // Push automated to current automated
        currentAutomated.push(automatedBroadcasts[abIndex]);
        // Push automated to list automated updated
        nextAutomated.push(automated);
      }
    }

    yield put({
      type: HANDLE_REMOVE_SUCCESS,
      automatedBroadcasts: newABs
    });
    // Push into UNDO_REDO list
    yield pushIntoUndoStack(currentAutomated, nextAutomated);
  } catch (e) {
    console.error(e.response || e);
    yield put({
      type: HANDLE_REMOVE_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
    // TODO: It should be return previous state when dropping error
  }
}

/**
 * Handle undo
 */
function* handleUndo() {
  try {
    const state = yield select(onboarderState);
    const newState = state.get('stateHistory').toJS();
    const newABs = state.get('automatedBroadcasts').toJS();
    if (newState.currentIndex < 0) { return; }
    newState.currentIndex -= 1;
    newState.canUndo = newState.currentIndex > -1;
    newState.canRedo = true;
    const automatedBackup = newState.recovery[newState.currentIndex + 1];

    automatedBackup.before.forEach((backup) => {
      const index = newABs.findIndex(ab => ab.id === backup.id);
      newABs[index] = backup;
    });

    yield put({
      type: UNDO_SUCCESS,
      stateHistory: newState,
      automatedBroadcasts: newABs
    });

    // Save automated broadcast
    yield all(automatedBackup.before.map(automated => updateAutomated(automated)));
  } catch (e) {
    console.error(e.response || e);
    yield put({
      type: UNDO_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
  }
}

/**
 * Handle redo
 */
function* handleRedo() {
  try {
    const state = yield select(onboarderState);
    const newState = state.get('stateHistory').toJS();
    const newABs = state.get('automatedBroadcasts').toJS();
    if (newState.currentIndex > newState.recovery.length - 1) { return; }
    newState.currentIndex += 1;
    newState.canUndo = true;
    newState.canRedo = newState.currentIndex < newState.recovery.length - 1;
    const automatedBackup = newState.recovery[newState.currentIndex];

    automatedBackup.after.forEach((backup) => {
      const index = newABs.findIndex(ab => ab.id === backup.id);
      newABs[index] = backup;
    });

    yield put({
      type: UNDO_SUCCESS,
      stateHistory: newState,
      automatedBroadcasts: newABs
    });

    // Save automated broadcast
    yield all(automatedBackup.after.map(automated => updateAutomated(automated)));
  } catch (e) {
    console.error(e.response || e);
    yield put({
      type: UNDO_FAILURE,
      appError: {
        status: e.response ? e.response.status : null,
        statusText: e.response ? e.response.statusText : '',
        data: {
          message: e.response ? e.response.data.message || e.response.data.error : e.message
        }
      }
    });
  }
}

/**
 * Capture change properties
 */
function* captureChangeProperties(action) {
  try {
    yield delay(1000);
    const state = yield select(onboarderState);
    const listABs = state.get('automatedBroadcasts').toJS();
    const automated = listABs.find(item => item.id === action.payload.abId);
    if (automated) {
      yield updateAutomated(automated);
    }
  } catch (e) {
    console.log(e.response || e);
  }
}

function* onboarderSaga() {
  yield takeLatest(FETCH_AUTOMATED_BROADCAST_REQUEST, fetchAutomatedBroadcasts);
  yield takeLatest(FETCH_AUDIENCES_REQUEST, fetchAudiences);
  yield takeLatest(SEARCH_AUTOMATED_BROADCAST_REQUEST, searchAutomatedBroadcast);
  yield takeLatest(HANDLE_DROP_REQUEST, handleDrop);
  yield takeLatest(HANDLE_REMOVE_REQUEST, handleRemove);
  yield takeLatest(CHANGE_PROPERTIES, captureChangeProperties);
  yield takeEvery(UNDO_REQUEST, handleUndo);
  yield takeEvery(REDO_REQUEST, handleRedo);
}

export default onboarderSaga;



// WEBPACK FOOTER //
// ./src/containers/onboarder/saga.js